import { ExportButton } from '@/common/buttons/ExportButton';
import { EntityList } from '@/common/entityFramework/entityList/EntityList';
import { useNavState } from '@/common/navState/NavStateContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Title } from '@/common/title/Title';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { Box, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const CalcProdCompare = () => {
  const { navState, navigateTo } = useNavState();
  const rsrc = useRsrc();
  const entityListRef = useRef();

  const [calcs, setCalcs] = useState([]);
  const [exportReq, setExportReq] = useState();

  const calcIds = useMemo(() => {
    if (Array.isArray(navState?.calcIds) && navState.calcIds.length > 0) {
      return navState.calcIds;
    }
    if (Array.isArray(navState?.calcs) && navState.calcs.length > 0) {
      return navState.calcs.map(calc => calc.id).filter(Boolean);
    }
    return [];
  }, [navState?.calcIds, navState?.calcs]);

  const calcKey = useMemo(() => calcIds.join('|'), [calcIds]);

  useEffect(() => {
    if (calcIds.length !== 2) {
      setCalcs([]);
      return;
    }

    const rowsReq = { expr: ExprUtils.whereParams('id', calcIds) };
    rsrc.rows('gp', 'CALC', rowsReq).then((calcRows) => {
      const sortedCalcs = new Array(calcIds.length);
      calcRows.forEach((calc) => {
        const index = calcIds.indexOf(calc.id);
        if (index >= 0) {
          sortedCalcs[index] = calc;
        }
      });
      setCalcs(sortedCalcs.filter(Boolean));
    });
  }, [calcKey]);

    useEffect(() => {
      const exportReq = {
      module: 'gp',
      calcIds: calcIds,
      uiType: 'CALC_COMPARE'
    };
    setExportReq(exportReq);
  }, []);

  const fieldDefs = useMemo(() => {
    if (calcs.length !== 2) {
      return [];
    }
    const [leftCalc, rightCalc] = calcs;
    return [
      {
        children: [
          { displayName: 'NDC11', field: 'ndc11ProdCode', maxWidth: 150 },
          { displayName: 'Name', field: 'name' },
        ]
      },
      {
        field: 'value0',
        displayName: (leftCalc?.name ?? 'Calculation A') + ' Value',
        type: 'MONEY',
        decimalPrecision: 6,
        valueGetter: (params) => toNumber(params.data?.value0),
      },
      {
        field: 'value1',
        displayName: (rightCalc?.name ?? 'Calculation B') + ' Value',
        type: 'MONEY',
        decimalPrecision: 6,
        valueGetter: (params) => toNumber(params.data?.value1),
      },
      {
        displayName: 'Comparison',
        marryChildren: true,
        children: [
          {
            field: 'match',
            displayName: 'Match',
            width: 70,
            cellClass: 'ag-center-cell',
            type: 'BOOLEAN',
            valueGetter: (params) => {
              const value0 = toNumber(params.data?.value0);
              const value1 = toNumber(params.data?.value1);
              return value0 != null && value1 != null && value0 === value1;
            },
          },
          {
            field: 'delta',
            displayName: 'Delta',
            width: 110,
            type: 'MONEY',
            decimalPrecision: 6,
            valueGetter: (params) => {
              const value0 = toNumber(params.data?.value0);
              const value1 = toNumber(params.data?.value1);
              return value0 != null && value1 != null ? value1 - value0 : null;
            },
          },
          {
            field: 'variance',
            displayName: 'Variance',
            width: 110,
            type: 'PERCENT',
            decimalPrecision: 2,
            valueGetter: (params) => {
              const value0 = toNumber(params.data?.value0);
              const value1 = toNumber(params.data?.value1);
              if (value0 == null || value0 === 0 || value1 == null) {
                return null;
              }
              return (value1 - value0) / value0;
            },
          },
        ]
      }
    ];
  }, [calcs]);

  const augRowsReq = useCallback((rowsReq) => {
    rowsReq.query = 'CALC_PROD_COMPARE';
    rowsReq.replaceParams = { leftCalcId: calcIds[0], rightCalcId: calcIds[1] };
  }, [calcIds]);

  const navToCalcProdResCompare = (data) => {
    navigateTo('gp/compare/calc-prod-res', {
      calcProdIds: [data.calcProdId0, data.calcProdId1], //should this be disabled if one is null?
      calcIds: calcIds
    });
  };

  const getContextMenuItems = (params) => {
    let contextMenuItems = [];
    contextMenuItems.push({
      name: 'Result Comparison',
      action: () => navToCalcProdResCompare(params.node.data),
      icon: fas.faBalanceScale,
    });
    return contextMenuItems;
  };

  if (calcs.length !== 2) {
    return (
      <Box sx={{ p: 2 }}>
        <Title icon={fas.faBalanceScale} title="Calculation Compare" />
        <Typography sx={{ mt: 2 }}>
          Select two calculations from the list to compare their product values.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between', width: '100%'}}>
      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <Title icon={fas.faBalanceScale} title="Calculation Compare" />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} >
          <ExportButton exportReq={exportReq} />
        </Box>
        </Box>
      </Box>
      <EntityList
        ref={entityListRef}
        module="gp"
        entity="CALC_PROD"
        showTitle={false}
        augRowsReq={augRowsReq}
        gridOptions={{
          fieldDefs,
          getContextMenuItems
        }}
      />
    </Box>
  );
};

const toNumber = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
};
